/**
 * The signed-in session.
 *
 * The whole app is behind a login: every RPC requires an operator, and the
 * shell will not render until one is signed in. Accounts live in the users
 * table — this module only carries the token the server issued.
 *
 * The token is a JWT, sent as `Authorization: Bearer` (RFC 6750). It lives in
 * localStorage rather than a cookie because the API is a separate origin in
 * every deployment shape we support, and a cross-site cookie would need
 * SameSite=None plus a shared parent domain; a header works the same
 * everywhere.
 */
import { apiOrigin } from './origin';

const TOKEN_STORAGE = 'travplaces.accessToken';

/** Fired when the server rejects the stored token, so the guard can re-ask. */
export const SESSION_EXPIRED_EVENT = 'travplaces:session-expired';

export interface SessionState {
  authenticated: boolean;
  /**
   * This deployment runs without accounts (TP_AUTH_ENABLED=false).
   *
   * Everybody is let in, so `authenticated` is true and the login form never
   * appears. It says more than that, though: there is nobody to sign out as,
   * no password to change, no operators to administer, and no name to show
   * against a saved plan. The parts of the app that only mean something where
   * there are accounts read this and leave themselves out.
   */
  authDisabled?: boolean;
  username?: string;
  role?: string;
  /**
   * What this operator may do, already expanded by the server — an admin
   * holding PERMISSION_ALL comes back holding everything it implies.
   *
   * Used to decide which controls to draw, never to decide whether an action
   * is allowed: the server checks every procedure again regardless. See
   * features/authorization.
   */
  permissions?: string[];
}

export function accessToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE);
  } catch {
    // Private windows and locked-down browsers throw rather than return
    // null. An operator who cannot store a token can still work, they will
    // just be asked to log in again on the next page load.
    return null;
  }
}

export function setAccessToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE, token);
  } catch {
    // Ignored for the reason above.
  }
}

export function clearAccessToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE);
  } catch {
    // Ignored for the reason above.
  }
}

/** Drops the token and tells the guard, after the server refuses it. */
export function expireSession(): void {
  clearAccessToken();
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/**
 * Asks the server whether the stored token is still good, and who it belongs
 * to. Called on load so the login form appears immediately rather than after
 * the first RPC fails.
 */
export async function fetchSession(signal?: AbortSignal): Promise<SessionState> {
  const token = accessToken();

  const response = await fetch(`${apiOrigin}/api/auth/session`, {
    signal,
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error('Could not reach the TravPlaces API.');
  }

  return (await response.json()) as SessionState;
}

/**
 * Exchanges a username and password for a session token, storing it on
 * success. Throws with a message fit to show the operator.
 */
export async function login(username: string, password: string): Promise<void> {
  let response: Response;

  try {
    response = await fetch(`${apiOrigin}/api/auth/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  } catch {
    throw new Error('Could not reach the TravPlaces API. Is the server running?');
  }

  const body = (await response.json().catch(() => null)) as {
    access_token?: string;
    error?: string;
  } | null;

  if (!response.ok || !body?.access_token) {
    throw new Error(body?.error ?? 'Could not sign in.');
  }

  setAccessToken(body.access_token);
}

export function logout(): void {
  clearAccessToken();
}
